import React, { useEffect } from 'react';
import { NoticeFormType, PublicNotice } from '../../types/notices';
import { Button, LinkButton } from '../../components/common';
import styled from 'styled-components/macro';
import { FlexColumn, FlexRow } from '../../components/basic/Flex';
import Pictogram from '../../components/common/Pictogram';
import { getBackgroundForNoticeType, getPictogramForNoticeType, NOTICE_PAGE_QUERY_PARAM } from './notice-utils';
import { ThemeColor, themeColors } from '../../theme/theme';
import { DeviceSize } from '../../components/basic/DeviceSize';
import { ActionPlanPreview, ActionReportPreview, MeetingMinutesPreview } from '../forms';
import { useMessageGetter } from 'react-message-context';
import { Divider } from '../../components/common/Divider';
import { RouteComponentProps, useLocation, useParams } from 'react-router';
import { useKennoTenant } from '../../context/kennoTenantContext';
import { loadPublicNotices } from '../../context/kennoTenantActions';
import PageContainer from '../PageContainer';
import PaddedContent from '../../components/housing/PaddedContent';
import { Slugs } from '../../constants/knownSlugs';
import { getMenuItemPathBySlug } from '../../utils/wordpress';
import { useAppState } from '../../context/appContext';

const Column = styled(FlexColumn)`
  width: 256px;

  @media print {
    display: none;
  }
`;

const LeftColumn = styled(Column)`
  @media ${DeviceSize.desktopS} {
    display: none;
  }
  @media ${DeviceSize.mobileL} {
    display: none;
  }
`;

const RightColumn = styled(Column)`
  @media ${DeviceSize.desktopS} {
    display: none;
  }
`;

const PictogramCircle = styled.div<{ background: ThemeColor }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 56px;
  background: ${(props) => themeColors[props.background]};
  border-radius: 1000px;
  border: 2px solid ${themeColors.darkSnow};

  @media ${DeviceSize.desktopM} {
    padding: 28px;
  }
`;

const ButtonRow = styled(FlexRow)`
  @media print {
    display: none;
  }
`;

const renderPreview = (notice: PublicNotice) => {
  switch (notice.formType) {
    case NoticeFormType.ActionPlan:
      return <ActionPlanPreview formData={notice.formData} />;
    case NoticeFormType.ActionReport:
      return <ActionReportPreview formData={notice.formData} />;
    case NoticeFormType.MeetingMinutes:
      return <MeetingMinutesPreview formData={notice.formData} />;
  }
};

const NoticePage = ({ history }: RouteComponentProps) => {
  const t = useMessageGetter('common');
  const { menu } = useAppState();
  const { id: noticeId } = useParams<{ id?: string }>();
  const [{ publicNotices }, dispatch] = useKennoTenant();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const queryParamPage = query.get(NOTICE_PAGE_QUERY_PARAM);

  useEffect(() => {
    loadPublicNotices(dispatch);
  }, [dispatch]);

  const notice = publicNotices.data?.find((notice) => notice.id === noticeId);

  if (!notice) return <></>;

  return (
    <PageContainer history={history}>
      <PaddedContent>
        <FlexRow gap="48px" justifyContent="space-between">
          <LeftColumn>
            <PictogramCircle background={getBackgroundForNoticeType(notice.formType)}>
              <Pictogram width={144} height={144} name={getPictogramForNoticeType(notice.formType)} />
            </PictogramCircle>
          </LeftColumn>
          <FlexColumn gap="16px">
            <FlexColumn>{renderPreview(notice)}</FlexColumn>
            <Divider />
            <ButtonRow gap="16px">
              <LinkButton
                to={`${getMenuItemPathBySlug(menu.items, Slugs.NOTICES)}?${NOTICE_PAGE_QUERY_PARAM}=${queryParamPage}`}
                icon="arrowBackward"
                primary
              >
                {t('back')}
              </LinkButton>
              <Button onClick={window.print} icon="print">
                {t('print')}
              </Button>
            </ButtonRow>
          </FlexColumn>
          <RightColumn />
        </FlexRow>
      </PaddedContent>
    </PageContainer>
  );
};

export default NoticePage;
